// ─────────────────────────────────────────────
//  useRoomBreakActions — shared room break actions (mobile redesign Wave 2)
//  MOVED VERBATIM from components/RoomCard.js so the mobile tree can reuse
//  the exact same break orchestration without forking it. The hook only
//  orchestrates: every break-state transition goes through the tested
//  src/domain/breaks.js machine, every write through the existing
//  src/firebase/firestore.js helpers, in the same order RoomCard used —
//  room doc first, then the /breaks record, then the alert resolve — so a
//  denied room write aborts before an orphan /breaks record exists.
//  Toasts fire here through the same ToastContext RoomCard/BreakEnginePanel
//  use (mobile mounts the same ToastProvider). Params in, actions out, no
//  JSX: callers own their popover/UI state and close it when an action
//  resolves true (actions resolve false after a failure toast, so a
//  caller's popover stays open exactly as RoomCard's did).
// ─────────────────────────────────────────────
import { useToast } from "../context/ToastContext";
import { useAlertCenter } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { ALERT_TYPES, isLiveAlert } from "../domain/alerts";
import {
  assignCover,
  arriveForCover,
  cancelCover,
  completeBreak,
  getChannelState,
  BREAK_CHANNELS,
  normalizeBreakChannel,
  stampBreakTime,
  nowHm,
} from "../domain/breaks";
import { assignCoverToRoom } from "../features/breaks/assignCoverToRoom";
import {
  setRoomBreak,
  endRoomBreak,
  requestBreak,
  updateBreak,
} from "../firebase/firestore";
import { breakCoverRecipients } from "../domain/breakDirective";
import { dayKey } from "../domain/dayKey";

// "lunch" → "Lunch" (toast + chip copy). Shared with RoomCard's render.
export const chLabel = (ch) => (ch ? ch.charAt(0).toUpperCase() + ch.slice(1) : "");

export default function useRoomBreakActions({
  room,
  assignment = null,
  providerName = "",
  suppressAlerts = false,
  // ⚠️ THE VIEWER'S STAFF IDS. A room and an assignment name their people by
  // `staff/{id}`; `uid` is a Firebase Auth id. Comparing the two directly is
  // the confusion that made Room Status unreachable for everyone on
  // 2026-09-07, and it is here too: without this, `iAmRoomProvider` is false
  // for every real clinician and the break controls never appear for the one
  // person entitled to press them. Optional and defaulting to empty, so every
  // existing caller behaves exactly as before.
  staffIds = null,
}) {
  const toast = useToast();
  const { user } = useAuth();
  // ⚠️ THE STAFF LIST COMES FROM THE CONTEXT, NOT A SECOND `useStaff()`. Rooms
  // and assignments name people by `staff/{id}` while every task and push keys
  // on the Firebase AUTH uid, so this hook needs the link — but it is mounted
  // once per ROOM CARD (fourteen on a real board, twice per row on the phone's
  // break board) and `useStaff` has no module cache, so calling it here opened
  // a duplicate whole-collection listener for every one of them. AlertContext
  // was already subscribed; it now publishes the list.
  const {
    alerts, uid: myUid, identityIds, staff,
    breakRequestRoomIds, raiseBreakRequest, resolveBreakRequestForRoom,
  } = useAlertCenter();
  const asg = assignment || {};

  // ── Identity (Prompts 3 & 9) ───────────────────
  // The logged-in provider is auto-captured as the COVER — no picker. Identity
  // from the real signed-in user (uid + name).
  const meId = user?.uid || myUid || null;
  const meName = user?.name || "";
  const crnaPid = asg.ahpId || room.providerId || null;
  // Matches on the auth uid OR any staff id resolved for this viewer. Fails
  // closed: an unresolved id matches nobody, because a break control offered to
  // somebody who is not in the room is a room that looks covered and is not.
  // ⚠️ ALWAYS A NEW SET. Taking the caller's Set and adding to it MUTATES a
  // memoized value the page holds (`useMemo(() => userStaffIds(...))`), so the
  // caller's own set silently gains the auth uid with no identity change and
  // nothing re-renders — order-dependent behaviour across renders, and a hook
  // writing to its argument.
  // ⚠️ FALLS BACK TO THE CONTEXT'S IDENTITY SET. Only 2 of the 8 call sites ever
  // passed `staffIds` — every mobile surface omitted it — so on a phone `myIds`
  // was the auth uid alone and `iAmCover` could only ever be true for a break
  // taken through `coverBreak`. A break assigned from a roster picker stores the
  // coverer's STAFF id, so the person standing in the room got no control over
  // their own break. The context has always known both id spaces; now it says so.
  const myIds = new Set(staffIds || identityIds || []);
  if (meId) myIds.add(meId);
  const namesMe = (id) => !!id && myIds.has(id);
  const iAmRoomProvider = !!meId && (namesMe(room.providerId) || namesMe(crnaPid));
  const iAmRoomDoctor = !!meId && (namesMe(room.supervisingDoctorId) || namesMe(asg.attendingId));
  const iAmOnThisRoom = iAmRoomProvider || iAmRoomDoctor;
  // Live break request for this room (drives the yellow blink + cover action).
  // Today-state — suppressed on the Tomorrow preview like the other alerts.
  const breakReqAlert = suppressAlerts
    ? null
    : alerts.find(
        (a) => a.type === ALERT_TYPES.BREAK_REQUEST && a.roomId === room.id && isLiveAlert(a)
      );
  const breakRequested = !suppressAlerts && (!!breakReqAlert || (breakRequestRoomIds && breakRequestRoomIds.has(room.id)));
  // Am I the person currently covering this room's break? Only I can end it.
  // ⚠️ `relieverId` HOLDS TWO DIFFERENT ID SPACES depending on which button
  // wrote it: `coverBreak` writes the auth uid, while `assignBreak` and the
  // give-a-break sheet write the STAFF id of whoever was picked from a roster.
  // Comparing with === therefore failed for every break assigned from a picker
  // — so the person actually standing in the room covering the break got no
  // "end my break" control, and only an AIC could close it out.
  const iAmCover = namesMe(room.relieverId);
  // The channel a reliever is currently covering, if any.
  const activeChannel = BREAK_CHANNELS.find(
    (ch) => getChannelState(room.breaks, ch) === "in_progress"
  );
  // The channel somebody has been ASKED to cover and has not yet arrived for.
  // ⚠️ A SEPARATE VALUE, NOT A WIDENED `activeChannel`. Every consumer of
  // `activeChannel` — the End-break control, the "X is covering" line, the
  // countdown — is about a break that is actually running. Widening it would
  // put an End button on a break nobody has started.
  const pendingChannel = BREAK_CHANNELS.find(
    (ch) => getChannelState(room.breaks, ch) === "assigned"
  );
  // Split from `iAmCover` because the two rights are different: the named cover
  // may ARRIVE or DECLINE while pending, and may END only once they are in the
  // room. One flag for both would offer "end my break" for a break that has not
  // begun.
  const iAmAssignedCover = iAmCover && !!pendingChannel;
  const iAmActiveCover = iAmCover && !!activeChannel;

  // ── Actions ────────────────────────────────────
  // Assign a break with an explicit cover. Doctors and CRNAs are auto-captured
  // as the cover (the logged-in person — giver defaults to me); a Board
  // Manager caller resolves the cover from its roster picker and passes it in.
  const assignBreak = async ({ channel, giver: giverIn } = {}) => {
    const giver = giverIn || (meId ? { id: meId, name: meName } : null);
    if (!channel || !giver?.id) return false;
    try {
      // WHO IS BEING RELIEVED — the room's CRNA, from the day's row first.
      const relievedId = crnaPid;
      // The ONE recipient rule, shared with the Timeline BREAKS card. Computed
      // BEFORE the write because the toast below has to know whether the person
      // being named can actually be reached.
      const recipients = breakCoverRecipients({
        staff,
        room,
        assignment: asg,
        giverId: giver.id,
        relievedId,
      });

      // ⚠️ `assigned`, NOT `in_progress`. This names somebody; it does not put
      // them in the room. `onBreak` stays false and the board keeps asking for
      // this room until the coverer's own arrival tap says they are there.
      //
      // Through the shared write, because the Timeline BREAKS card names covers
      // too and is not inside a room — two hand-written copies of this is how
      // BreakEnginePanel and VoiceControl drifted from the card before.
      // WHO TO TELL. Three people: the coverer is instructed and is the only
      // one asked to answer; the provider being relieved and the room's
      // supervising anaesthesiologist are informed. See domain/breakDirective.
      await assignCoverToRoom(room, {
        channel,
        giver,
        notify: {
          facilityId: room.facilityId,
          // The FACILITY's day, not the device's. `requestBreak` stamps a
          // device-local key and the sheet engine uses the row's own — three
          // definitions of "today" already exist here; this one is the tz-aware
          // one that `useTasks` reads back.
          dateKey: dayKey(),
          senderId: meId,
          senderName: meName,
          roomName: room.name || "",
          channelLabel: chLabel(channel),
          giverName: giver.name || "",
          relievedName: providerName || "",
          ...recipients,
        },
      });
      // If this fulfilled a live break request, persist the break record and
      // clear the yellow request alert (same close-out coverBreak does).
      if (breakReqAlert) {
        try {
          await requestBreak({
            facilityId: room.facilityId,
            roomId: room.id,
            type: channel,
            giverId: breakReqAlert?.giverId || room.providerId || null,
            giverName: breakReqAlert?.giverName || providerName || "",
            receiverId: giver.id,
            receiverName: giver.name,
            // ⚠️ NOT `startedAt`. Nothing has started — somebody was asked.
            // The break's real start is stamped when they arrive.
            assignedAt: new Date().toISOString(),
          });
        } catch (recErr) { console.error("Break record failed:", recErr); }
        // ⚠️ THE REQUEST ALERT STAYS UP UNTIL SOMEBODY ARRIVES. Clearing it here
        // would extinguish the only artifact tracking a room that is still
        // uncovered — the CRNA has been told help is coming and has stopped
        // asking, which is worse than never having been told. `resolve` moved
        // to the arrival transition.
      }
      // ⚠️ DO NOT PROMISE A MESSAGE THAT WAS NEVER SENT. `uidOf` returns null
      // for somebody with no login — a pool-only staff record — and
      // `buildBreakCoverTasks` then correctly writes nothing for them. Saying
      // "is on the way" regardless told the AIC the person had been asked when
      // no phone had rung and no directive existed, so nothing would escalate
      // either: the one case where the board must speak up is the one where it
      // used to be most confident. The cover IS named either way; what changes
      // is whether anybody still has to make the phone call.
      if (recipients.giverUid) {
        toast.success(`${giver.name} is on the way to cover ${chLabel(channel)} in ${room.name}.`);
      } else {
        toast.success(
          `${giver.name} is named for ${chLabel(channel)} in ${room.name} — no app account, so tell them yourself.`
        );
      }
      return true;
    } catch (err) {
      console.error("Assign break failed:", err);
      toast.error(err.message || "Could not assign the break.");
      return false;
    }
  };

  // The named cover is in the room and has taken handoff. THIS is the write
  // that makes the board's claim of coverage true, and only the named coverer
  // may make it — an AIC confirming an arrival from a desk re-creates exactly
  // the lie this split removes, one step later.
  const arriveBreak = async () => {
    const channel = pendingChannel;
    if (!channel) return false;
    if (!iAmCover) {
      toast.error("Only the person assigned to cover this break can start it.");
      return false;
    }
    try {
      const breaks = arriveForCover(room.breaks || {}, channel);
      // Re-stamped on arrival: `breakTimes` records the moment a channel last
      // changed hands, and this is the change that matters — the assign-time
      // stamp was the age of the ask, which the board has already spent.
      const breakTimes = stampBreakTime(room.breakTimes, channel, nowHm());
      await setRoomBreak(room.id, {
        breaks, channel, breakTimes,
        relieverId: room.relieverId, relieverName: room.relieverName,
        onBreak: true,
      });
      // NOW the room is covered, so the request is genuinely answered.
      try { await resolveBreakRequestForRoom(room.id); } catch (e) { console.error(e); }
      toast.success(`You're covering ${chLabel(channel)} for ${room.name}.`);
      return true;
    } catch (err) {
      console.error("Arrive for cover failed:", err);
      toast.error(err.message || "Could not start the break.");
      return false;
    }
  };

  // The assigned cover never came, was stood down, or the case turned and the
  // break must be called off. DELIBERATELY WIDER than the arrival right: a
  // break is refused for clinical reasons by the people in the room, not by the
  // person walking over. The room's provider, its supervising anaesthesiologist,
  // an AIC/board manager, or the assigned coverer declining may all call it.
  const cancelBreakCover = async () => {
    const channel = pendingChannel;
    if (!channel) return false;
    try {
      // A provider-requested bathroom goes back to `requested`, not to `none`:
      // the need did not go away, only the timing did.
      const wasRequested = !!breakReqAlert;
      const breaks = cancelCover(room.breaks || {}, channel, { wasRequested });
      await setRoomBreak(room.id, {
        breaks, channel,
        relieverId: null, relieverName: "",
        onBreak: false,
      });
      toast.info(`Cover for ${chLabel(channel)} in ${room.name} was called off.`);
      return true;
    } catch (err) {
      console.error("Cancel cover failed:", err);
      toast.error(err.message || "Could not call off the cover.");
      return false;
    }
  };

  // Phase 5.3 — completing a supervisor-approved break also closes its C-003
  // /breaks record (status "completed") so the cycle persists end-to-end.
  const closeBreakRecord = async () => {
    if (!room.activeBreakId) return;
    try {
      await updateBreak(room.activeBreakId, {
        status: "completed",
        completedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error("Close break record failed:", err);
    }
  };

  const endBreak = async () => {
    // ⚠️ AN ASSIGNED-BUT-UNARRIVED BREAK IS CANCELLED, NOT ENDED. Falling
    // through to the no-op below would return `true` — the caller closes its
    // popover as though the break had been dealt with, while the room stays
    // assigned to somebody who never came. `completeBreak` refuses this state
    // for the same reason: ending it would lock a meal green for a provider
    // who never left the room.
    if (!activeChannel && pendingChannel) return cancelBreakCover();
    if (!activeChannel) {
      // Nothing running — nothing to write; the caller just closes its UI.
      return true;
    }
    try {
      const breaks = completeBreak(room.breaks || {}, activeChannel);
      // ⚠️ THE STAMP IS NOT TOUCHED ON THE WAY OUT. `breakTimes[channel]` was
      // written on assign, again on arrival, AND again here — so one key meant
      // "when this break started" while it ran and "when it ended" afterwards.
      // PanelRoomCard renders it as "✓ 11:55" beside a finished meal under a
      // comment reading "when it happened", which made that number the moment
      // the CRNA came BACK. Ending a break leaves the start time standing; the
      // end belongs on the /breaks record, which already has completedAt.
      await endRoomBreak(room.id, { breaks, channel: activeChannel });
      await closeBreakRecord();
      // Clear any lingering pool broadcast for this room (5.3).
      try { await resolveBreakRequestForRoom(room.id); } catch (e) { console.error(e); }
      toast.info(`Break ended for ${room.name}.`);
      return true;
    } catch (err) {
      console.error("End break failed:", err);
      toast.error("Could not end the break.");
      return false;
    }
  };

  // Request a break for my own room → raises the yellow alert (Prompt 9).
  const requestMyBreak = async (channel) => {
    const doctorId = room.supervisingDoctorId || asg.attendingId || null;
    const res = await raiseBreakRequest({
      roomId: room.id,
      roomName: room.name,
      doctorId,
      giverId: meId,
      giverName: meName || providerName,
      breakType: channel,
    });
    if (res?.ok) {
      toast.success(`Break requested for ${room.name}. Waiting for coverage…`);
      return true;
    }
    if (res?.reason === "duplicate") {
      toast.info(`A break is already requested for ${room.name}.`);
    } else {
      toast.error("Could not request a break.");
    }
    return false;
  };

  // Call off a break I asked for myself.
  //
  // ⚠️ THE PROVIDER COULD NOT TAKE THEIR OWN REQUEST BACK. Owner, 2026-09-11:
  // "we have to find a way that the provider can cancel the break request
  // himself." Withdrawing existed only on the web Timeline BREAKS card, gated
  // on `canManageAssignments` — an AIC. So a CRNA who tapped "I need a break"
  // and then no longer needed one (the case ran long, the bench emptied, they
  // ate at the desk) had NO WAY BACK from their own phone: the red chip sat on
  // the board until somebody else cleared it, and the only exit was accepting a
  // break they did not want. A request nobody can retract teaches people not to
  // raise one.
  //
  // ⚠️ IT IS THE REQUEST, NOT A BREAK STATE. `raiseBreakRequest` never writes
  // the room doc — the request lives ONLY in the alert bus — so calling it off
  // is resolving those alerts, exactly as the Timeline card does. Nothing about
  // `room.breaks` changes, and a channel already marked done stays done.
  //
  // ⚠️ REFUSED ONCE SOMEBODY IS COMING. `activeChannel` / `pendingChannel` mean
  // a cover has been named or has arrived; cancelling then would clear the
  // request out from under a colleague already walking to the room, and they
  // would find out by arriving. Calling off a COVER is `cancelBreakCover`, a
  // different action with a different consequence — see the same split on the
  // Timeline card.
  const cancelMyBreakRequest = async () => {
    if (!iAmRoomProvider || !room?.id) return false;
    if (!breakRequested) return false;
    if (activeChannel || pendingChannel) {
      toast.info("Relief is already on the way — ask them directly if plans changed.");
      return false;
    }
    try {
      await resolveBreakRequestForRoom(room.id);
      toast.success(`Break request for ${room.name} cancelled.`);
      return true;
    } catch (e) {
      // The machine's own words: "permission-denied" tells the reader what to
      // do and "could not cancel" does not.
      console.error("Cancel my break request failed:", e);
      toast.error(e?.message || "Could not cancel the request.");
      return false;
    }
  };

  // Take over the break — I become the cover (auto-captured). Clears the alert.
  const coverBreak = async () => {
    // Normalize the alert's stored type onto its canonical channel so the
    // board card marks EXACTLY the break that was requested (bathroom stays
    // bathroom — never a lunch fallback from a case-mismatched string).
    const channel = normalizeBreakChannel(breakReqAlert?.breakType) || activeChannel || "lunch";
    try {
      // ⚠️ A CLAIM, NOT AN ARRIVAL. Tapping "I'll take it" is what stops three
      // people walking to the same room and stripping three others — that job
      // is done the instant it is tapped, from the lounge or anywhere else.
      // Being in the room is a separate physical fact, and only the room can
      // confirm it. So this lands in `assigned` like any other cover, and the
      // same person's arrival tap starts the clock.
      const breaks = assignCover(room.breaks || {}, channel);
      const breakTimes = stampBreakTime(room.breakTimes, channel, nowHm());
      await setRoomBreak(room.id, {
        breaks, channel, breakTimes,
        relieverId: meId, relieverName: meName,
        onBreak: false,
      });
      // Break record: room, person on break (giver), type, and the cover (me).
      try {
        await requestBreak({
          facilityId: room.facilityId,
          roomId: room.id,
          type: channel,
          giverId: breakReqAlert?.giverId || room.providerId || null,
          giverName: breakReqAlert?.giverName || providerName || "",
          receiverId: meId,
          receiverName: meName,
          // Nothing has started — I have said I will go. See assignBreak.
          assignedAt: new Date().toISOString(),
        });
      } catch (recErr) { console.error("Break record failed:", recErr); }
      // ⚠️ THE ALERT STAYS UP. Clearing it on the claim would tell the board a
      // room is handled while it is still uncovered. It clears on arrival.
      toast.success(`You're on the way to cover ${chLabel(channel)} in ${room.name}.`);
      return true;
    } catch (err) {
      console.error("Cover break failed:", err);
      toast.error("Could not take over the break.");
      return false;
    }
  };

  // End MY break coverage → normal restored, alert cleared. Only the cover.
  const endMyBreak = async () => {
    // ⚠️ NEVER "END" A BREAK THAT NEVER STARTED. While the cover is still only
    // assigned, this control is a DECLINE — completing it would record a meal
    // as taken by a provider who never left the room.
    if (!activeChannel && pendingChannel) return cancelBreakCover();
    const ch = activeChannel || normalizeBreakChannel(breakReqAlert?.breakType);
    try {
      const breaks = ch ? completeBreak(room.breaks || {}, ch) : (room.breaks || {});
      // Same rule as endBreak: the start time survives the break ending.
      await endRoomBreak(room.id, { breaks, channel: ch });
      await closeBreakRecord();
      await resolveBreakRequestForRoom(room.id);
      toast.info(`Break ended for ${room.name}. Normal status restored.`);
      return true;
    } catch (err) {
      console.error("End my break failed:", err);
      toast.error("Could not end the break.");
      return false;
    }
  };

  return {
    meId,
    meName,
    iAmRoomProvider,
    iAmRoomDoctor,
    iAmOnThisRoom,
    iAmCover,
    iAmAssignedCover,
    iAmActiveCover,
    breakReqAlert,
    breakRequested,
    activeChannel,
    pendingChannel,
    assignBreak,
    arriveBreak,
    cancelBreakCover,
    endBreak,
    requestMyBreak,
    cancelMyBreakRequest,
    coverBreak,
    endMyBreak,
    closeBreakRecord,
  };
}
